/**
 * Chat Store
 *
 * Zustand store for persisting chat state across page navigations.
 * Stores messages, session ID, RAG pipeline context, and chat mode
 * in localStorage so the user can navigate away and return without
 * losing their conversation.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { RAGPipelineState } from '@/lib/api/rag-pipeline-service';

// Upper bound on how many of the most-recent chat messages we persist to
// localStorage (T3.2). Does NOT limit the in-memory conversation.
const MAX_PERSISTED_MESSAGES = 50;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ChatMessage {
  /**
   * Result of a workspace metadata query, when one actually ran. Absent means
   * the turn was NOT grounded in account data — the evidence card keys off
   * this, so an ungrounded answer cannot display one.
   */
  metadataQuery?: {
    type: 'workspace_metadata';
    title: string;
    entity: string;
    row_count: number;
    content: string;
  };
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  isSystem?: boolean;
  sources?: Array<{
    id: string;
    content: string;
    score: number;
    source_file?: string;
    page_number?: number;
    document_id?: string;
    metadata?: Record<string, any>;
  }>;
  // Governed agent-runtime chat (CHAT-CH1): guardrail/HITL state from
  // POST /agents/runtime/{agent_id}/chat. All optional/additive so the
  // RAG and general-chat branches are unaffected.
  blocked?: boolean;
  violations?: string[];
  pendingApproval?: boolean;
  // KB that produced `sources` above (RAG-mode messages only) — lets the
  // sources UI build a "view document" deep link to /knowledgebases?id=.
  // Agent-mode messages have no KB context, so this stays undefined there.
  sourcesKbId?: string;
  // Knowledgebases (chat 'kb' mode): the KB the sources came from, for
  // /knowledgebases deep links, and the retrieval facts the evidence panel
  // renders as the trace of that turn.
  sourcesLibraryId?: string;
  kbTrace?: { mode: string; rounds: number; stop_reason?: string | null; release_number?: number | null; considered?: number; context_tokens?: number; dense?: number; lexical?: number; graph?: number; cost_coverage?: string; cited?: number; status?: string; grader_error?: string | null };
  // Per-turn evidence captured from the stream `done` frame (previously
  // discarded). Powers the operator evidence panel: per-message cost, model,
  // token usage, measured wall-clock latency, and the run id for deep-linking
  // into observability. All optional/additive.
  costUsd?: number;
  model?: string;
  tokens?: { prompt?: number; completion?: number; total?: number };
  latencyMs?: number;
  runId?: string;
  // WS-5: the agent answered through a single-LLM-call path (quick chat / quick
  // mode / test chat), so configured tools, MCP servers, knowledgebases and
  // sub-agents were NOT used. Set only when the agent actually has some of those
  // configured; the message renders a subtle notice so the reply isn't mistaken
  // for the agent's full, grounded behaviour.
  capabilityNotice?: string;
  capabilitiesOmitted?: string[];
}

export interface ChatStoreState {
  // Persisted state
  messages: ChatMessage[];
  sessionId: string | null;
  ragPipeline: RAGPipelineState | null;
  chatMode: 'general' | 'rag';

  // Actions
  setMessages: (messages: ChatMessage[]) => void;
  addMessage: (message: ChatMessage) => void;
  setSessionId: (sessionId: string | null) => void;
  setRagPipeline: (pipeline: RAGPipelineState | null) => void;
  setChatMode: (mode: 'general' | 'rag') => void;
  updateRagPipelineStatus: (status: RAGPipelineState['status'], error?: string) => void;
  clearChat: () => void;
}

// ============================================================================
// STORE
// ============================================================================

export const useChatStore = create<ChatStoreState>()(
  persist(
    (set, get) => ({
      // Initial state
      messages: [],
      sessionId: null,
      ragPipeline: null,
      chatMode: 'general',

      // Actions
      setMessages: (messages) => set({ messages }),

      addMessage: (message) =>
        set((state) => ({ messages: [...state.messages, message] })),

      setSessionId: (sessionId) => set({ sessionId }),

      setRagPipeline: (pipeline) =>
        set({
          ragPipeline: pipeline,
          chatMode: pipeline ? 'rag' : get().chatMode,
        }),

      setChatMode: (mode) => set({ chatMode: mode }),

      updateRagPipelineStatus: (status, error) =>
        set((state) => {
          if (!state.ragPipeline) return state;
          return {
            ragPipeline: { ...state.ragPipeline, status, error },
          };
        }),

      clearChat: () =>
        set({
          messages: [],
          sessionId: null,
          ragPipeline: null,
          chatMode: 'general',
        }),
    }),
    {
      name: 'chat-storage',

      // T3.2 (FE plan): cap the PERSISTED slice only. The live in-memory
      // `messages` array still grows without limit during a session — this
      // bounds just what we write to (and rehydrate from) localStorage, so a
      // long conversation can't bloat storage to MBs and slow every page
      // hydrate. The cap is generous; only the oldest messages of a very long
      // history are dropped from persistence on navigate-away.
      partialize: (state) => {
        const recent = state.messages.slice(-MAX_PERSISTED_MESSAGES);
        // Only persist a conversation that has at least one REAL message.
        // `isSystem` messages are ephemeral UI (agent/KB greeting seeds, upload
        // and ingestion status, error notices). A conversation that is *only*
        // a seeded greeting must not survive navigation — otherwise a stale
        // agent greeting ("Hi! I'm Tenant Intake Agent…") rehydrates on the
        // next plain /chat visit and masquerades as an active agent even
        // though no agent is connected. Real exchanges persist in full.
        const hasRealMessage = recent.some((m) => !m.isSystem);
        return {
          messages: hasRealMessage ? recent : [],
          sessionId: hasRealMessage ? state.sessionId : null,
          ragPipeline: state.ragPipeline,
          chatMode: state.chatMode,
        };
      },

      storage: {
        getItem: (name: string) => {
          if (typeof window === 'undefined') return null;
          try {
            const item = localStorage.getItem(name);
            return item ? JSON.parse(item) : null;
          } catch {
            return null;
          }
        },
        setItem: (name: string, value: any) => {
          if (typeof window === 'undefined') return;
          try {
            localStorage.setItem(name, JSON.stringify(value));
          } catch {
            // Storage full or unavailable
          }
        },
        removeItem: (name: string) => {
          if (typeof window === 'undefined') return;
          try {
            localStorage.removeItem(name);
          } catch {
            // Ignore
          }
        },
      },

      version: 1,

      /**
       * Skip auto-hydrate so SSR and first client render match. `AuthProvider`
       * calls `useChatStore.persist.rehydrate()` after mount. Without this
       * flag, the chat page renders a different tree on the client during
       * hydration (persisted messages/sessionId/ragPipeline), tripping
       * React #418.
       */
      skipHydration: true,

      migrate: (persistedState: any, version: number) => {
        return persistedState as ChatStoreState;
      },
    }
  )
);
